import React from "react";
import styled from "styled-components";
import { assertNever } from "../../amorg-common";
import { Button } from "../button/Button";
import { colors } from "../colors/color";
import { getIconName, iconName } from "../icons/Icons";
import { ButtonVariant } from "../button/Shared";

const MENU_ITEM_ICON_WIDTH_PX = 20;

export type PopupMenuButtonEntry =
    | { type: 'separator' }
    | { type: 'label', text: string }
    | { type: 'menuItem', icon?: iconName, text: string, reserveIconSpace?:boolean, disabled?: boolean, onClick?: () => void };

export type PopupMenuButtonProps = {
    entries: PopupMenuButtonEntry[],
    buttonText?: string,
    buttonIcon?: iconName,
    buttonVariant?:ButtonVariant,
    buttonClassName?: string
    disabled?: boolean,
}
export function PopupMenuButton(props: PopupMenuButtonProps): JSX.Element
{
    const [open, setOpen] = React.useState(false);
    const rootRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        function handleMouseDown(event: MouseEvent)
        {
            if (rootRef.current && !rootRef.current.contains(event.target as Node))
            {
                setOpen(false);
            }
        }

        function handleEscape(event: KeyboardEvent)
        {
            if (event.key === 'Escape')
            {
                setOpen(false);
            }
        }

        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    function renderEntry(entry: PopupMenuButtonProps['entries'][number], index: number): JSX.Element
    {
        if (entry.type === 'separator')
        {
            return <div key={`separator-${index}`} style={{ width: '100%', borderTop: `1px solid ${colors.divider.default}` }} />;
        }

        if (entry.type === 'label')
        {
            return (
                <div key={`label-${index}`} style={{ fontSize: 12, fontWeight: 600, padding: '4px 8px', color: colors.text.light }}>
                    {entry.text}
                </div>
            );
        }

        if (entry.type === 'menuItem')
        {
            return (
                <MenuItemButton

                    key={`item-${index}`}
                    onClick={() => {
                        setOpen(false);
                        entry.onClick?.();
                    }}
                    disabled={entry.disabled}
                >
                    {(entry.icon || entry.reserveIconSpace) &&
                        <MenuItemIconSlot>
                            {entry.icon && getIconName(entry.icon)}
                        </MenuItemIconSlot>
                    }
                    {entry.text}
                </MenuItemButton>
            );
        }

        return assertNever(entry);
    }

    return (
        <div ref={rootRef} style={{ position: 'relative', display: 'inline-block' }}>
            <Button
                variant={props.buttonVariant || 'text'}
                icon={props.buttonIcon ?? 'navigation_context'}
                text={props.buttonText ?? ''}
                disabled={props.disabled}
                onClick={() => setOpen(current => !current)}
                className={props.buttonClassName}
            />

            {open &&
                <div
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: 'calc(100% + 4px)',
                        minWidth: 160,
                        border: `1px solid ${colors.menu.border}`,
                        borderRadius: 4,
                        background: colors.menu.background,
                        padding: 6,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 4,
                        zIndex: 1000
                    }}
                >
                    {props.entries.map((entry, index) => renderEntry(entry, index))}
                </div>
            }
        </div>
    );
}

const MenuItemButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 4px 8px;
    background: none;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    font-family: inherit;
    color: ${props => props.theme.colors.text.normal};
    cursor: pointer;
    text-align: left;
    white-space: nowrap;

    &:hover {
        background: ${props => props.theme.colors.button.text.backgroundHover};
    }

    &:disabled {
        color: ${props => props.theme.colors.text.muted};
        cursor: not-allowed;
    }
`;

const MenuItemIconSlot = styled.span`
    display: flex;
    align-items: center;
    width: ${MENU_ITEM_ICON_WIDTH_PX}px;
    flex-shrink: 0;

    & svg {
        width: ${MENU_ITEM_ICON_WIDTH_PX}px;
        height: ${MENU_ITEM_ICON_WIDTH_PX}px;
        fill: currentColor;
    }
`;